import React from "react";
import { MouseEvent, MouseEventHandler } from "react";
import { Link, matchPath } from "react-router-dom";

import { Location, LocationDescriptorObject } from "history";

import { IAuthProps } from "./AuthContext";
import withAuth from "./withAuth";

export interface IMenuItemProps extends IAuthProps {
    header: React.ReactNode;
    link?: string;
    selected?: boolean;
    expanded?: boolean;
    location?: Location;
    exact?: boolean;
    match?: string | string[];
    state?: LocationDescriptorObject<any>;

    rights?: string | string[];
    rightsAny?: string | string[];

    className?: string;
    onClick?: MouseEventHandler;
}

interface IMenuItemState {
    expanded?: boolean;
}

class MenuItem extends React.Component<IMenuItemProps, IMenuItemState> {
    constructor(props: Readonly<IMenuItemProps>) {
        super(props);

        this.state = {
            expanded: false
        };

        this.toggleMenu = this.toggleMenu.bind(this);
    }

    public render() {
        if (this.props.rights) {
            if (typeof this.props.rights === "string") {
                if (!this.props.authContext.hasRight(this.props.rights)) {
                    return "";
                }
            }
            else if (!this.props.rights.every(r => this.props.authContext.hasRight(r))) {
                return "";
            }
        }
        if (this.props.rightsAny) {
            if (typeof this.props.rightsAny === "string") {
                if (!this.props.authContext.hasRight(this.props.rightsAny)) {
                    return "";
                }
            }
            else if (!this.props.rightsAny.some(r => this.props.authContext.hasRight(r))) {
                return "";
            }
        }

        if (this.props.children) {
            const path = (this.props.location && this.props.location.pathname) || "";
            let shouldExpand = false;

            const children = React.Children.map(this.props.children, c => {
                if (!React.isValidElement(c)) {
                    return c;
                }

                const props = c.props as IMenuItemProps;
                const match = (props.match && (typeof props.match === "string" ? [props.match] : props.match)) || (props.link ? [props.link] : undefined);
                const shouldSelect = match && match.find(m => matchPath(path, { path: m, exact: props.exact }) !== null);
                if (shouldSelect) {
                    shouldExpand = true;
                }

                return React.cloneElement(c, { selected: shouldSelect }/* as IMenuItemProps*/ as any);
            });

            let anyChildShown = false;
            React.Children.forEach(this.props.children, c => {
                if (!React.isValidElement(c)) {
                    return;
                }

                const props = c.props as IMenuItemProps;
                const hasAllRights = !props.rights || (typeof props.rights === "string" ? this.props.authContext.hasRight(props.rights) : props.rights.every(r => this.props.authContext.hasRight(r)));
                const hasAnyRights = !props.rightsAny || (typeof props.rightsAny === "string" ? this.props.authContext.hasRight(props.rightsAny) : props.rightsAny.some(r => this.props.authContext.hasRight(r)))
                if (hasAllRights && hasAnyRights) {
                    anyChildShown = true;
                }
            });

            // If none of our children are allowed to be shown, don't show us either
            if (!anyChildShown) {
                return "";
            }

            const expand = this.state.expanded || shouldExpand;

            return (
                <li className={(expand ? "menu-expanded" : "") + (this.props.selected ? " menu-selected" : "")}>
                    {
                        this.props.link ?
                            (<Link to={{ pathname: this.props.link, state: this.props.state }} className={`menu-item ${this.props.className ? this.props.className : ""}`} onClick={this.toggleMenu}>{this.props.header}</Link>) :
                            (<header className={`menu-item ${this.props.className ? this.props.className : ""}`} tabIndex={0} onClick={this.toggleMenu}>{this.props.header}</header>)
                    }
                    <ul className="menu-inner menu-inner-sub">
                        {children}
                    </ul>
                </li>
            );
        }

        return (
            <li className={(this.props.selected ? " menu-selected" : "")}>
                {
                    this.props.link ?
                        (<Link to={{ pathname: this.props.link, state: this.props.state }} className={`menu-item ${this.props.className ? this.props.className : ""}`}>{this.props.header}</Link>) :
                        (<header className={`menu-item ${this.props.className ? this.props.className : ""}`} onClick={this.props.onClick}>{this.props.header}</header>)
                }
            </li>
        );
    }

    private toggleMenu(event?: MouseEvent<Element>) {
        if (this.props.onClick && event) {
            this.props.onClick(event);
            return;
        }

        this.setState(state => ({
            expanded: !state.expanded
        }));
    }
}

export default withAuth(MenuItem);